import React from "react";
import FactureInput from "./FactureInput2";
import VentilationMainPanel from "./VentilationMainPanel";
import { Modal } from "react-bootstrap";
import { useFacture } from "../hooks/useFacture";
import FactureRèglementsComp from "./FactureRèglementsComp";
import { trpc } from "../../main/components/ProviderContainer.tsx";
import factureStateToFacture from "../processing/factureStateToFacture.ts";
import { factureValidate } from "compta-shared";
import type {
  ExistingFacture3,
  FactureDuplicateCandidate,
} from "compta-shared";
import DuplicateFactureWarningModal, { getDuplicateConfirmationKeys } from "./DuplicateFactureWarningModal.tsx";
import ConfirmModal from "./ConfirmModal.tsx";
import { DateTime } from "luxon";

interface MainFactureInputPanelProps {
  selectedFacture: ExistingFacture3 | null;
  onDone: () => void;
}

const MainFactureInputPanel = (props: MainFactureInputPanelProps) => {
  const { facture, ventilationsProps, factureProps, règlementsProps } =
    useFacture(props.selectedFacture || null);
  const accountingPeriodsQuery = trpc.accountingPeriod.list.useQuery();
  const effectiveSinglePaymentDate = facture.datePrelevement || facture.date;
  const effectiveAccountingDates =
    facture.règlements.length > 0
      ? facture.règlements.flatMap((règlement) =>
          règlement.date ? [règlement.date] : [],
        )
      : effectiveSinglePaymentDate
        ? [effectiveSinglePaymentDate]
        : [];
  const unavailableAccountingYear = effectiveAccountingDates
    .map((date) => DateTime.fromJSDate(date).setZone("Europe/Paris").year)
    .find((year) => {
      const period = accountingPeriodsQuery.data?.find(
        (candidate) => candidate.year === year,
      );
      return period ? period.status !== "open" : year <= 2025;
    });
  const accountingPeriodSaveBlocked =
    accountingPeriodsQuery.isLoading ||
    accountingPeriodsQuery.isError ||
    unavailableAccountingYear !== undefined;
  const accountingPeriodSaveBlockedReason = accountingPeriodsQuery.isError
    ? "État des exercices indisponible"
    : accountingPeriodsQuery.isLoading
      ? "Chargement des exercices"
      : unavailableAccountingYear !== undefined
        ? `Exercice ${unavailableAccountingYear} non ouvert`
        : null;
  const isFactureLocked =
    props.selectedFacture?.locked === true || facture.locked === true;
  const hasLockedRèglements = facture.règlements.some(
    (règlement) => règlement.locked === true,
  );
  const commonFieldsLocked = isFactureLocked || hasLockedRèglements;
  const structureLocked = commonFieldsLocked;
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [ventilationModalOpen, setVentilationModalOpen] = React.useState(false);
  const [showAlertSuccess, setShowAlertSuccess] = React.useState(false);
  const [showAlertError, setShowAlertError] = React.useState(false);
  const [returnToPayError, setReturnToPayError] = React.useState<string | null>(
    null,
  );
  const [returnToPayConfirmationOpen, setReturnToPayConfirmationOpen] =
    React.useState(false);
  const [duplicateCandidates, setDuplicateCandidates] = React.useState<
    FactureDuplicateCandidate[]
  >([]);
  const [pendingCreatedFacture, setPendingCreatedFacture] =
    React.useState<ReturnType<typeof factureStateToFacture> | null>(null);
  const {
    ventilations,
    totalActuelVentilationsCent,
    ventilationTotalTargetCent,
    ventilationsErrors,
    onSetCurrentVentilationSelected,
    onUpdateVentilation,
    onDeleteVentilation,
    onDeleteAllVentilations,
    onAcceptVentilations,
  } = ventilationsProps;

  const { setIsNotTouched, isTouched } = factureProps;
  const currentVentilationLocked =
    isFactureLocked ||
    (facture.currentVentilationSelected === "main"
      ? commonFieldsLocked
      : typeof facture.currentVentilationSelected === "number"
        ? facture.règlements[facture.currentVentilationSelected]?.locked === true
        : false);

  console.log("isTouched", isTouched);
  const handleCancelVentilations = () => {
    setVentilationModalOpen(false);
  };

  const handleDeleteAllVentilations = () => {
    if (currentVentilationLocked) {
      return;
    }
    setVentilationModalOpen(false);
    onDeleteAllVentilations();
    onSetCurrentVentilationSelected(null);
  };

  const handleAcceptVentilations = () => {
    if (currentVentilationLocked) {
      return;
    }
    setVentilationModalOpen(false);
    onAcceptVentilations();
    onSetCurrentVentilationSelected(null);
  };
  const onCancelEditFacture = () => {
    factureProps.onCancelCurrentFacture();
    props.onDone();
  };
  const onOpenGeneralVentilations = () => {
    if (commonFieldsLocked) {
      return;
    }
    setVentilationModalOpen(true);
    onSetCurrentVentilationSelected("main");
  };
  const onOpenLineVentilations = (idx: number) => {
    if (isFactureLocked || facture.règlements[idx]?.locked === true) {
      return;
    }
    onSetCurrentVentilationSelected(idx);
    setVentilationModalOpen(true);
  };
  const showRèglementsInput = facture.règlements.length > 0;
  const { onConditionnalyAddRèglementLine } = règlementsProps;
  const toggleRèglementsInput = () => {
    if (structureLocked) {
      return;
    }
    onConditionnalyAddRèglementLine(0);
  };

  const linesIndexWithVentilations = facture.règlements.map(
    (règlement) => règlement.ventilations.length > 0,
  );

  const utils = trpc.useContext();
  const factureEditedCommitMutation = trpc.facture.commitEdited.useMutation({
    onSuccess: () => {
      setIsSubmitting(false);
      setShowAlertSuccess(true);
      factureProps.onCancelCurrentFacture(); //on remet à zéro après un commit réussi
      setTimeout(() => {
        setShowAlertSuccess(false);
      }, 3000);
      utils.facture.invalidate();
      utils.libellé.invalidate();
      setIsNotTouched();
      props.onDone();
    },
    onError: () => {
      setIsSubmitting(false);
      setShowAlertError(true);
      setTimeout(() => {
        setShowAlertError(false);
      }, 3000);
    },
  });

  const factureCreateMutation = trpc.facture.create.useMutation({
    onSuccess: () => {
      setIsSubmitting(false);
      setShowAlertSuccess(true);
      factureProps.onCancelCurrentFacture(); //on remet à zéro après un commit réussi
      setTimeout(() => {
        setShowAlertSuccess(false);
      }, 3000);
      utils.facture.invalidate();
      utils.libellé.invalidate();
      setIsNotTouched();
      props.onDone();
    },
    onError: () => {
      setIsSubmitting(false);
      setShowAlertError(true);
      setTimeout(() => {
        setShowAlertError(false);
      }, 3000);
    },
  });

  const factureDeleteMutation = trpc.facture.delete.useMutation({
    onSuccess: () => {
      setIsSubmitting(false);
      setShowAlertSuccess(true);
      factureProps.onCancelCurrentFacture(); //on remet à zéro après un commit réussi
      setTimeout(() => {
        setShowAlertSuccess(false);
      }, 3000);
      utils.facture.invalidate();
      utils.libellé.invalidate();
      setIsNotTouched();
      props.onDone();
    },
    onError: () => {
      setIsSubmitting(false);
      setShowAlertError(true);
      setTimeout(() => {
        setShowAlertError(false);
      }, 3000);
    },
  });
  const returnToPayMutation = trpc.facture.returnToPay.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.facture.invalidate(),
        utils.superPdp.invalidate(),
        utils.libellé.invalidate(),
      ]);
      factureProps.onCancelCurrentFacture();
      setIsNotTouched();
      props.onDone();
    },
    onError: (error) => {
      setReturnToPayError(error.message);
    },
  });

  const confirmCreateFacture = (
    factureToCreate: ReturnType<typeof factureStateToFacture>,
    confirmedDuplicateKeys: string[] = [],
  ) => {
    if (accountingPeriodSaveBlocked) {
      return;
    }
    setIsSubmitting(true);
    factureCreateMutation.mutate({ ...factureToCreate, confirmedDuplicateKeys }, {
      onSettled: () => {
        setDuplicateCandidates([]);
        setPendingCreatedFacture(null);
      },
    });
  };

  const onCommitFacture = async () => {
    if (commonFieldsLocked || accountingPeriodSaveBlocked) {
      return;
    }
    const factureCleaned = factureStateToFacture(facture);
    if (!factureValidate(factureCleaned)) {
      throw new Error("facture invalide dans commitFacture");
    }
    if (facture._id && facture.owner) {
      setIsSubmitting(true);
      factureEditedCommitMutation.mutate({
        ...factureCleaned,
        _id: facture._id,
        owner: facture.owner,
      });
    } else {
      try {
        const duplicates = await utils.facture.checkBusinessDuplicates.fetch({
          modeRef: factureCleaned.modeRef,
          date: factureCleaned.date,
          datePrelevement: factureCleaned.datePrelevement || null,
          montantCent: factureCleaned.montantCent,
        });
        if (duplicates.length > 0) {
          setDuplicateCandidates(duplicates);
          setPendingCreatedFacture(factureCleaned);
          return;
        }
        confirmCreateFacture(factureCleaned);
      } catch {
        setShowAlertError(true);
        setTimeout(() => {
          setShowAlertError(false);
        }, 3000);
      }
    }
  };

  const onDeleteFacture = () => {
    if (structureLocked) {
      return;
    }
    const _id = facture._id;
    if (!_id) {
      throw new Error("facture._id est null dans onDeleteFacture");
    }
    setIsSubmitting(true);
    factureDeleteMutation.mutate(_id);
  };
  const onReturnToPay = () => {
    if (structureLocked || isTouched || !facture._id || !facture.docId) {
      return;
    }
    setReturnToPayError(null);
    setReturnToPayConfirmationOpen(true);
  };
  const confirmReturnToPay = () => {
    if (!facture._id) {
      return;
    }
    returnToPayMutation.mutate(
      { factureId: facture._id },
      {
        onSettled: () => {
          setReturnToPayConfirmationOpen(false);
        },
      },
    );
  };
  return (
    <div>
      <FactureInput
        {...factureProps}
        facture={facture}
        onOpenGeneralVentilations={onOpenGeneralVentilations}
        hasVentilations={facture.ventilations.length > 0}
        onToggleRèglements={toggleRèglementsInput}
        onCommitFacture={onCommitFacture}
        isSubmitting={isSubmitting}
        onCancelEdit={onCancelEditFacture}
        onDelete={onDeleteFacture}
        onReturnToPay={onReturnToPay}
        isLocked={commonFieldsLocked}
        commonFieldsLocked={commonFieldsLocked}
        structureLocked={structureLocked}
        saveBlockedReason={accountingPeriodSaveBlockedReason}
      />
      {showAlertSuccess && (
        <div className="alert alert-success" role="alert">
          Enregistré avec succès
        </div>
      )}
      {showAlertError && (
        <div className="alert alert-danger" role="alert">
          Il y a eu un problème lors de l'enregistrement de la facture
        </div>
      )}
      {returnToPayError ? (
        <div className="alert alert-danger" role="alert">
          {returnToPayError}
        </div>
      ) : null}
      {showRèglementsInput && (
        <FactureRèglementsComp
          {...règlementsProps}
          règlements={facture.règlements}
          onOpenVentilations={onOpenLineVentilations}
          linesIndexWithVentilations={linesIndexWithVentilations}
          isLocked={commonFieldsLocked}
          structureLocked={structureLocked}
        />
      )}

      <Modal
        show={ventilationModalOpen}
        dialogClassName="modalWidthClass"
        fullscreen="sm-down"
      >
        <VentilationMainPanel
          ventilations={ventilations}
          montantCent={ventilationTotalTargetCent}
          totalActuelVentilationsCent={totalActuelVentilationsCent}
          onValueChange={onUpdateVentilation}
          onDeleteVentilation={onDeleteVentilation}
          onDeleteAllVentilations={handleDeleteAllVentilations}
          errors={ventilationsErrors}
          onCancelVentilations={handleCancelVentilations}
          acceptVentilations={handleAcceptVentilations}
          deleteAllEnabled={ventilations.length > 0}
          similarLinesIds={[]}
          onSetSimilarLinesIds={() => {}}
        />
      </Modal>
      <DuplicateFactureWarningModal
        show={duplicateCandidates.length > 0 && !!pendingCreatedFacture}
        items={
          duplicateCandidates.length > 0
            ? [
                {
                  title: "Facture en cours de création",
                  duplicates: duplicateCandidates,
                },
              ]
            : []
        }
        onCancel={() => {
          setDuplicateCandidates([]);
          setPendingCreatedFacture(null);
        }}
        onConfirm={() => {
          if (!pendingCreatedFacture) {
            return;
          }
          confirmCreateFacture(pendingCreatedFacture, getDuplicateConfirmationKeys(duplicateCandidates));
        }}
        confirmLabel="Créer la facture quand même"
        isSubmitting={factureCreateMutation.isPending}
      />
      <ConfirmModal
        show={returnToPayConfirmationOpen}
        title="Remettre la facture à payer"
        onCancel={() => setReturnToPayConfirmationOpen(false)}
        onConfirm={confirmReturnToPay}
        confirmLabel="Remettre à payer"
        submittingLabel="Remise à payer..."
        confirmVariant="warning"
        isSubmitting={returnToPayMutation.isPending}
      >
        La facture sera retirée des factures comptabilisées et remise dans la
        liste « À payer ». Ses données comptables seront conservées pour le
        prochain règlement.
      </ConfirmModal>
    </div>
  );
};

export default MainFactureInputPanel;
